import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateVenueMutation,
  useGetVenueQuery,
  useUpdateVenueMutation
} from '../../../../../services/endpoints/places/venue';
import {
  useGetAllZipcodesQuery,
  useLazyGetAllZipcodesQuery
} from '../../../../../services/endpoints/places/zipcode';
import { SFCVenue, VenueContact } from '../../../../../types/place.types';
import { phoneRegExp } from '../../../../../utils/consts';
import {
  getErrorMessage,
} from '../../../../../utils/utils';
import { getFormUpdateWithIdArrays } from '../../../../../utils/formDiffUtils';
import { getCurrentUser } from '../../../../../services/helper';
import { canViewVenues } from '../../../../../utils/rbac';
import { useDataContext } from '../../../../../context';
import { FadeIn } from '../../../../animations/FadeIn';
import { VenueForm } from './VenueForm';

export interface FormValues {
  id?: number;
  title: string;
  is_active: boolean;
  location_id: { value: number; label: string };
  organization_ids: { value: number; label: string }[];
  address: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  notes?: string;
  facility_use_fee_payment_details?: string;
  contacts: VenueContact[];
}

type Overwrite<T, NewT> = Omit<T, keyof NewT> & NewT;

export type CreateFormValues = Overwrite<
    FormValues,
    {
      organization_ids?: number[];
      location_id: number;
    }
>;

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

export const ContactSchema = Yup.object({
  first_name: Yup.string().required('First Name is required'),
  last_name: Yup.string().required('Last Name is required'),
  title: Yup.string().optional(),
  phone_number: Yup.string()
      .matches(phoneRegExp, 'Phone number is not valid')
      .min(10, 'Phone number should have at least 10 digits')
      .optional(),
  email: Yup.string().email('Email is invalid').required('Email is required'),
  should_be_sent_roster: Yup.boolean().required().default(false),
  should_be_sent_invoices: Yup.boolean().required().default(false),
  should_be_sent_bulk_welcomes: Yup.boolean().required().default(false),
  should_be_sent_bulk_thanks: Yup.boolean().required().default(false)
});

const toVenuePayload = (formValue: FormValues): CreateFormValues => {
  const payload: CreateFormValues = {
    title: formValue.title,
    is_active: formValue.is_active,
    location_id: formValue.location_id.value,
    organization_ids: formValue.organization_ids.map((o) => o.value),
    address: formValue.address ?? '',
    address2: formValue.address2 ?? '',
    city: formValue.city,
    state: formValue.state,
    zip: formValue.zip,
    phone: formValue.phone ?? '',
    notes: formValue.notes ?? '',
    facility_use_fee_payment_details: formValue.facility_use_fee_payment_details ?? '',
    contacts: formValue.contacts.map(({ full_name, ...contact }) => contact),
  };

  if (payload.facility_use_fee_payment_details === '') {
    delete payload.facility_use_fee_payment_details;
  }

  return payload;
};

const toVenueBaseline = (venue: SFCVenue): CreateFormValues => ({
  title: venue.title,
  is_active: venue.is_active,
  location_id: venue.location?.id ?? venue.location_id,
  organization_ids: venue.organizations?.map((o) => o.id) ?? [],
  address: venue.address ?? '',
  address2: venue.address2 ?? '',
  city: venue.city,
  state: venue.state,
  zip: venue.zip,
  phone: venue.phone ?? '',
  notes: venue.notes ?? '',
  facility_use_fee_payment_details: venue.facility_use_fee_payment_details ?? '',
  contacts: venue.contacts ?? [],
});

interface AddEditVenueProps {
  editing?: boolean;
}

export const AddEditVenue: React.FC<AddEditVenueProps> = ({ editing }) => {
  const params = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const { getLocationTitle } = useDataContext();

  const [createVenue] = useCreateVenueMutation();
  const [updateVenue] = useUpdateVenueMutation();
  const { data, error, isLoading } = useGetVenueQuery(Number(params.id!), {
    skip: !editing || !params.id
  });

  const [claimedZips, setClaimedZips] = useState<string[]>([]);
  const {
    data: claimedZipCodes,
    error: claimedZipsError,
    isLoading: claimedZipsLoading
  } = useGetAllZipcodesQuery({ scope: 'taken' });
  const [fetchAvailableZipCodes] = useLazyGetAllZipcodesQuery();

  useEffect(() => {
    if (claimedZipCodes && claimedZipCodes.results) {
      setClaimedZips(claimedZipCodes.results.map((z) => z.code));
    }
  }, [claimedZipCodes]);

  const getZipAvailable = async (value: any, context: any) => {
    if (typeof value !== 'string' || value.length !== 5) return false;

    const {
      location_id: { value: location_id }
    } = context.parent;
    let claimedZip = claimedZips.includes(value);
    if (claimedZip) {
      if (location_id) {
        let payload = {
          data: { filterBy: [{ field: 'location_id', operator: 'equals', value: location_id }] }
        };
        let availableZips = await fetchAvailableZipCodes(payload, true).unwrap();
        let available = availableZips?.results?.map((z) => z.code).includes(value);
        if (available) {
          // claimed by this location/franchise
          return true;
        } else {
          // claimed by another location/franchise
          return context.createError({
            message: 'This slice of the pie 🥧 has already been taken'
          });
        }
      } else {
        // claimed, but location has not been set
        return context.createError({
          message: 'Please select a location before entering the zip code'
        });
      }
    } else {
      // unclaimed, warn, but ok
      return context.createError({
        message: "Warning: This zip is outside this location's purchased territories"
      });
    }
  };

  const [initialValues, setInitialValues] = useState<FormValues>({
    title: '',
    is_active: true,
    location_id: { value: 0, label: '' },
    organization_ids: [],
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    notes: '',
    facility_use_fee_payment_details: '',
    contacts: []
  });

  const validationSchemas = [
    Yup.object({
      title: Yup.string().required('Title is required'),
      is_active: Yup.boolean().required().default(true),
      location_id: Yup.object({
        value: Yup.number().min(1, 'Location is required').required('Location is required'),
        label: Yup.string().optional()
      }).required('Location is required'),
      organization_ids: Yup.array()
          .of(
              Yup.object({ value: Yup.number().positive().required(), label: Yup.string().optional() })
          )
          .optional(),
      address: Yup.string().nullable().optional(),
      address2: Yup.string().nullable().optional(),
      city: Yup.string().required('City is required'),
      state: Yup.string().required('State is required'),
      zip: Yup.string().matches(/^\d{5}$/, 'Value must be exactly 5 digits'), //TODO: is not working the zipcode validation: //test('is-available', 'Unknown error occurred', getZipAvailable).required('Zip Code is required'),
      phone: Yup.string()
          .matches(phoneRegExp, 'Phone number is not valid')
          .min(10, 'Phone number should have at least 10 digits')
          .optional(),
      notes: Yup.string().nullable().optional(),
      facility_use_fee_payment_details: Yup.string().nullable().optional(),
      contacts: Yup.array().of(ContactSchema).optional()
    })
  ];

  useEffect(() => {
    if (!editing && currentUser && !currentUser.isGlobal && currentUser.location_id) {
      setInitialValues((prev) => ({
        ...prev,
        location_id: {
          value: currentUser.location_id,
          label: getLocationTitle(currentUser.location_id)
        }
      }));
    }
  }, [editing, currentUser, getLocationTitle]);

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading session', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.venue) {
      const { venue } = data;
      let values = {
        ...venue,
        ...{
          organization_ids: venue.organizations?.map((o) => ({ value: o.id, label: o.name })) ?? []
        },
        ...{ location_id: { value: venue?.location?.id ?? 0, label: venue?.location?.title ?? '' } }
      };
      setInitialValues(values);
    }
  }, [data]);

  const handleAddVenue = async (formValue: FormValues) => {
    try {
      let submission = toVenuePayload(formValue);
      if (!submission.location_id && currentUser?.location_id && !currentUser.isGlobal) {
        submission.location_id = currentUser.location_id;
      }
      if (!editing && submission.organization_ids?.length === 0) {
        delete submission.organization_ids;
      }

      try {
        if (editing) {
          if (!data?.venue) return;

          const baseline = toVenueBaseline(data.venue);
          let update = getFormUpdateWithIdArrays(baseline, submission, ['organization_ids']);
          let submitUpdate = Object.keys(update).length > 0;
          if (submitUpdate) {
            update.id = data.venue.id;
            const res = await updateVenue(update as CreateFormValues).unwrap();
            if (res && res.venue) {
              addToast(`Venue updated succesfully`, 'success');
            }
          } else {
            addToast(`No changes to Venue`, 'info');
          }
          navigate(`/admin/places/venues/${data?.venue.id}`);
        } else {
          const res = await createVenue(submission).unwrap();
          if (res && res.venue) {
            navigate(`/admin/places/venues/${res.venue.id}`);
            addToast('Venue created succesfully', 'success');
          } else throw new Error('A problem happened while creating Venue');
        }
      } catch (e) {
        addToast(getErrorMessage(e), 'error');
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  // RBAC: Admin, Location Admin, Global Admin, Super Admin, Supreme Admin
  if (!canViewVenues(currentUser)) {
    return <Forbidden />;
  }

  return (
      <FadeIn className="p-lg-4">
        <Stack
            gap={3}
            className="py-lg-0 px-4 py-2"
        >
          <div>
            <BackButton
                text={`Back to venue${editing ? ' details' : 's'}`}
                color="#2B4E64"
                url={editing ? `/admin/places/venues/${data?.venue.id}` : '/admin/places/venues'}
            />
            <h2
                className="d-none d-lg-block text-info fw-bold mb-0"
                style={{ fontSize: '2.25rem' }}
            >
              {editing ? 'Edit' : 'New'} Venue
            </h2>
          </div>

          <div
              className="p-lg-5 p-3"
              style={{
                boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
                border: '1px solid #EBEBEB',
                borderRadius: 10
              }}
          >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchemas[0]}
                onSubmit={handleAddVenue}
                enableReinitialize
            >
              {({
                  submitForm,
                  isSubmitting,
                  touched,
                  errors,
                  values,
                  setFieldValue
                }: FormikProps<FormValues>) => (
                  <FormikForm className="text-start">
                    <div style={{ minHeight: '65vh' }}>
                      <VenueForm
                          errors={errors}
                          touched={touched}
                          values={values}
                          setFieldValue={setFieldValue}
                      />
                    </div>

                    <Row className="justify-content-end mt-3">
                      <Col
                          xs={6}
                          lg={3}
                      >
                        <SimpleButton
                            disabled={isSubmitting}
                            type="button"
                            onClick={submitForm}
                            className="w-100"
                            variant="primary"
                        >
                          {editing ? 'Save' : 'Create'}
                        </SimpleButton>
                      </Col>
                    </Row>
                  </FormikForm>
              )}
            </Formik>
          </div>
        </Stack>
      </FadeIn>
  );
};